// Number and text rendering for the OTU table's CSV (`OtuCubeExport`).

export const CSV_SEPARATOR = ';'

/** Quote a value — always, with inner quotes doubled. */
export function csvQuote(value: string): string {
  return `"${value.replace(/"/g, '""')}"`
}

/**
 * Render a number for a CSV cell: the browser locale's decimal separator, but **no grouping
 * separator**. Grouping would put a stray `,` (en locales) inside an unquoted field, and spreadsheet
 * import dialogs commonly split on `,` and `;` at once — which shifts every column to its right.
 */
export const exportNumber = (value: number): string => numberFormat()(value)

/** {@link exportNumber} for a percent shown to two decimals. */
export const exportPercent = (value: number): string => percentFormat().format(value)

// One formatter per page: `toLocaleString` builds a new one per call, which a read matrix makes millions of.
const lazy = <T>(make: () => T): (() => T) => {
  let value: T | undefined
  return () => (value ??= make())
}
/**
 * A number for a CSV cell: at most three decimals of the double's exact binary value, an exact tie rounded half to
 * even, trailing zeros dropped, and the locale's decimal separator. [locale] defaults to the browser's.
 */
export function exportNumberFormatter(locale?: string): (value: number) => string {
  const decimal = new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.'
  return (value) => {
    if (!Number.isFinite(value)) return Number.isNaN(value) ? '\uFFFD' : value > 0 ? '∞' : '-∞'
    let fixed = value.toFixed(3)
    // `toFixed` rounds the exact binary value too, but a tie away from zero; a tie in the fourth decimal is x/16, x odd.
    const sixteenths = Math.abs(value) * 16
    if (Number.isInteger(sixteenths) && sixteenths % 2 === 1) {
      const floor = Math.floor(Math.abs(value) * 1000)
      const even = floor % 2 === 0 ? floor : floor + 1
      fixed = (value < 0 ? '-' : '') + (even / 1000).toFixed(3)
    }
    const trimmed = fixed.replace(/\.?0+$/, '')
    return decimal === '.' ? trimmed : trimmed.replace('.', decimal)
  }
}

const numberFormat = lazy(() => exportNumberFormatter())
const percentFormat = lazy(() => new Intl.NumberFormat(undefined, { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 }))
